import './section_7_main_content.css';
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Accordion from '~go/web/src/js/components/common/accordion/accordion';
import { AccordionContext } from 'react-bootstrap';
import Text from '~go/web/src/js/components/common/text/text';
import useBS5Breakpoints from '~go/web/src/js/hooks/use_bs5_breakpoints';
import { clsx } from '~go/web/src/js/utils/clsx/clsx';
import { motion } from 'motion/react';
import { handleEnterKeyDown } from '~go/web/src/js/utils/Helpers';
import section7Content from './section_7_content.json';

const { section7Items1, section7Items2 } = section7Content;

function Section7MainContent() {
  const [activeKey1, setActiveKey1] = useState(section7Items1[0].title);
  const [activeKey2, setActiveKey2] = useState(section7Items2[0].title);

  const handleSelect = eventKey => {
    setActiveKey1(eventKey);
  };
  const handleSelect2 = eventKey => {
    setActiveKey2(eventKey);
  };

  return (
    <div>
      <Accordion className="mt-2" defaultActiveKey={section7Items1[0].title} activeKey={activeKey1}>
        <Wrapper items={section7Items1} accordionPlacement="right" className="mt-5">
          {section7Items1.map((item, index) => (
            <Section7Item
              key={item.title}
              item={item}
              isLastItem={index === section7Items1.length - 1}
              onSelect={handleSelect}
            />
          ))}
        </Wrapper>
      </Accordion>

      <Accordion className="mt-2" defaultActiveKey={section7Items2[0].title} activeKey={activeKey2}>
        <Wrapper items={section7Items2} accordionPlacement="left" className="mt-10">
          {section7Items2.map((item, index) => (
            <Section7Item
              key={item.title}
              item={item}
              isLastItem={index === section7Items2.length - 1}
              onSelect={handleSelect2}
            />
          ))}
        </Wrapper>
      </Accordion>
    </div>
  );
}

export default Section7MainContent;

function Wrapper({ items, accordionPlacement, className, children }) {
  const currentActiveKey = useContext(AccordionContext);
  let activeItem = items.find(item => item.title === currentActiveKey);
  if (!activeItem) activeItem = items[0];

  const isMdBreakpoint = useBS5Breakpoints({ breakpoint: 'md' });
  const isLgBreakpoint = useBS5Breakpoints({ breakpoint: 'lg' });
  const isXlBreakpoint = useBS5Breakpoints({ breakpoint: 'xl' });

  const showImageAsOtherGridColumn = isMdBreakpoint;
  let display = 'block';
  if (showImageAsOtherGridColumn) display = 'grid';

  let columnGap = 0;
  if (isMdBreakpoint) columnGap = 30;
  if (isLgBreakpoint) columnGap = 120;

  let gridTemplateColumns = '1.2fr 1fr';
  if (accordionPlacement === 'left') {
    gridTemplateColumns = '1fr 1.2fr';
  }

  let fixedHeightForAccordion = null;
  if (isMdBreakpoint) fixedHeightForAccordion = 590;
  if (isLgBreakpoint) fixedHeightForAccordion = 560;
  if (isXlBreakpoint) fixedHeightForAccordion = 510;

  // 2 slots to hold images which lets us animate fade in/out between previous/new images
  const [imageToShowInFirstSlot, setImageToShowInFirstSlot] = useState(activeItem);
  const [imageToShowInSecondSlot, setImageToShowInSecondSlot] = useState(null);
  const [activeSlot, setActiveSlot] = useState(1);

  /**
   * When active item changes, assign that item to the currently inactive slot,
   * and then make that slot active, and make the currently active slot inactive.
   */
  useEffect(() => {
    if (activeSlot === 1) {
      setImageToShowInSecondSlot(activeItem);
    } else {
      setImageToShowInFirstSlot(activeItem);
    }

    setActiveSlot(prev => (prev === 1 ? 2 : 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem.title]);

  // Bottom should be 0 only for this item
  const isDataIndexingControlsActive = activeItem.title === 'Data indexing controls';

  let imageForMdAndAbove = null;
  if (showImageAsOtherGridColumn) {
    imageForMdAndAbove = (
      <div
        className="position-relative d-flex justify-content-center align-items-center bg-really-light-gray rounded-lg min-w-0 w-100"
        style={{}}
      >
        <motion.div
          variants={{
            bottom40: { bottom: 40 },
            bottom0: { bottom: 0 },
          }}
          animate={isDataIndexingControlsActive ? 'bottom0' : 'bottom40'}
          transition={{ duration: 0.2, ease: 'linear' }}
          className="position-absolute"
          style={{ inset: 40 }}
        >
          {/* 1st image slot */}
          <motion.img
            src={imageToShowInFirstSlot?.image}
            alt={imageToShowInFirstSlot?.imageAlt}
            className="w-100 h-100 position-absolute"
            style={{ objectFit: 'contain', top: 0, left: 0, bottom: 0, right: 0 }}
            variants={{
              hidden: { opacity: 0, transition: { duration: 0.2 } },
              visible: { opacity: 1, transition: { duration: 0.35 } },
            }}
            initial="hidden"
            animate={activeSlot === 1 ? 'visible' : 'hidden'}
          />
          {/* 2nd image slot */}
          <motion.img
            src={imageToShowInSecondSlot?.image}
            alt={imageToShowInSecondSlot?.imageAlt}
            className="w-100 h-100 position-absolute"
            style={{ objectFit: 'contain', top: 0, left: 0, bottom: 0, right: 0 }}
            variants={{
              hidden: { opacity: 0, transition: { duration: 0.2 } },
              visible: { opacity: 1, transition: { duration: 0.35 } },
            }}
            initial="hidden"
            animate={activeSlot === 2 ? 'visible' : 'hidden'}
          />
        </motion.div>
      </div>
    );
  }

  return (
    <div className={clsx(className)} style={{ display, gridTemplateColumns, columnGap }}>
      {accordionPlacement === 'right' && imageForMdAndAbove}

      <div style={{ height: fixedHeightForAccordion }}>{children}</div>

      {accordionPlacement === 'left' && imageForMdAndAbove}
    </div>
  );
}

Wrapper.defaultProps = {
  accordionPlacement: 'left',
  className: '',
};

Wrapper.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  accordionPlacement: PropTypes.oneOf(['left', 'right']),
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

function Section7Item({ item, isLastItem, onSelect }) {
  const currentActiveKey = useContext(AccordionContext);
  const isActive = currentActiveKey === item.title;

  const borderStyles = { borderTop: '1px solid var(--gray)' };
  if (isActive) {
    borderStyles.borderTop = '3px solid var(--dark)';
  }
  if (isLastItem) {
    borderStyles.borderBottom = '1px solid var(--gray)';
  }

  const isMdBreakpoint = useBS5Breakpoints({ breakpoint: 'md' });
  const showImageAsPartOfCollapse = !isMdBreakpoint;

  const imageForSmAndBelow = (
    <motion.div
      className="d-flex justify-content-center align-items-center bg-really-light-gray rounded-lg min-w-0 w-100 mt-2"
      style={{ aspectRatio: '1 / 1.2' }}
      variants={{
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
      }}
      animate={isActive ? 'visible' : 'hidden'}
      transition={{ duration: 0.3, delay: 0.1 }}
    >
      <img src={item.image} alt={item.imageAlt} className="p-3 p-sm-5 w-100 h-100" style={{ objectFit: 'contain' }} />
    </motion.div>
  );

  const handleToggleClick = () => {
    onSelect(item.title);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={clsx('section-7-item position-relative py-3', isActive ? 'cursor-default' : 'cursor-pointer')}
      style={borderStyles}
      onClick={handleToggleClick}
      onKeyDown={handleEnterKeyDown(handleToggleClick)}
    >
      <Text component="div" variant="marketing-h4">
        {item.title}
      </Text>

      <Accordion.Collapse eventKey={item.title}>
        <>
          <Text component="div" variant="marketing-sm" color="tertiary-text" className="mt-1">
            {item.description}
          </Text>

          {showImageAsPartOfCollapse && imageForSmAndBelow}
        </>
      </Accordion.Collapse>
    </div>
  );
}

Section7Item.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  item: PropTypes.object.isRequired,
  isLastItem: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};
